import type { Image, ImageStock } from "../models/image";

export type ImageFormat = "avif" | "webp";

export type ImageMeta = {
  image?: Image;
  publishedAt: number;
};

export type ImageOptions = {
  seed?: number | string;
  format?: ImageFormat;
  width?: number;
  height?: number;
  quality?: number;
};

const DEFAULT_WIDTH = 768;
const DEFAULT_QUALITY = 50;

const HTML_ESCAPES: Record<string, string> = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };

const escapeHtml = (text: string): string => text.replace(/[&<>"']/g, (char) => HTML_ESCAPES[char] ?? char);

/** The photographer's profile link, only when it is an https URL (anything else, `javascript:` included, becomes Unsplash's). */
const safeProfileUrl = (url: string): string => {
  try {
    return new URL(url).protocol === "https:" ? url : "https://unsplash.com";
  } catch {
    return "https://unsplash.com";
  }
};

/** The credit line pages render as HTML, so the photographer's name and profile link are escaped. */
export const buildImageAttributionHtml = (image: ImageStock): string => {
  const unsplash = "https://unsplash.com?utm_source=nottldr&utm_medium=referral";
  const profile = escapeHtml(safeProfileUrl(image.userProfile));
  return `Image by <a href="${profile}" target="_blank" rel="noopener noreferrer">${escapeHtml(image.user)}</a> on <a href="${escapeHtml(unsplash)}" target="_blank" rel="noopener noreferrer">Unsplash</a>`;
};

export const getImageUrl = (
  src: string | undefined,
  seed: number | string,
  format: ImageFormat = "avif",
  width: number = DEFAULT_WIDTH,
  quality: number = DEFAULT_QUALITY,
): string => {
  const source = src ?? `https://picsum.photos/800/1000?random=${seed}`;
  if (!source.includes("unsplash.com")) {
    return source;
  }

  const url = new URL(source);
  url.searchParams.set("fm", format);
  url.searchParams.set("w", width.toString());
  url.searchParams.set("q", quality.toString());
  return url.toString();
};

export const getHeroImageUrl = (meta: ImageMeta, format: ImageFormat = "avif"): string => {
  return getImageUrl(meta.image?.src, meta.publishedAt, format);
};

/** Like `getImageUrl`, with an optional height (which also turns on `fit=crop`). */
export const getImageUrl2 = (src: string | undefined, options: ImageOptions = {}): string => {
  const { seed, format = "avif", width = DEFAULT_WIDTH, height, quality = DEFAULT_QUALITY } = options;
  const source = src ?? `https://picsum.photos/${width}/${height ?? width / 0.8}?random=${seed ?? Math.round(Math.random() * 1000000)}`;
  if (!source.includes("unsplash.com")) {
    return source;
  }

  const url = new URL(source);
  const params = Object.fromEntries(url.searchParams.entries());
  params.fm = format;
  params.w = width.toString();
  params.q = quality.toString();
  if (height) {
    params.h = height.toString();
    params.fit = "crop";
  }
  return new URL(`https://${url.hostname}${url.pathname}?${new URLSearchParams(params).toString()}`).toString();
};
